import { useTranslation } from 'react-i18next';
import type { AssetBalanceDto } from '@shared/api/contracts';
import { formatLedgerAmount } from '@shared/lib/format-ledger-amount';
import { subtractNonNegativeDecimal } from '@shared/lib/parse-decimal';
import { formatAssetNetworkLabel, getAssetBalanceKey } from '@shared/lib/asset-balance';
import { isUnallocatedNetwork } from '@shared/constants/network';

interface BalanceCardsProps {
  balances: AssetBalanceDto[];
}

export function BalanceCards({ balances }: BalanceCardsProps) {
  const { t } = useTranslation('cabinet');
  const historicalLabel = t('balance.historicalNetwork');

  return (
    <section className="space-y-3">
      <h2 className="section-title">{t('balance.byAssetTitle')}</h2>
      {!balances.length ? (
        <p className="text-sm text-muted-foreground">{t('balance.byAssetEmpty')}</p>
      ) : (
        <div className="flex flex-wrap gap-2 sm:gap-3">
          {balances.map((balance) => {
            const ticker = balance.assetCode;
            const iconSrc = balance.currency?.icon_base;
            const networkLabel = formatAssetNetworkLabel(balance.network, historicalLabel);
            const reserved = balance.availableAmount == null
              ? null
              : subtractNonNegativeDecimal(balance.amount, balance.availableAmount);

            return (
              <div
                key={getAssetBalanceKey(balance.assetCode, balance.network)}
                className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 shadow-sm sm:rounded-xl sm:px-4 sm:py-3"
              >
                {iconSrc ? (
                  <img src={iconSrc} alt={ticker} className="h-5 w-5 sm:h-6 sm:w-6" />
                ) : (
                  <div className="h-5 w-5 rounded-full bg-muted sm:h-6 sm:w-6" />
                )}
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">{t('balance.total')}</p>
                  <span className="font-sans text-sm font-semibold text-base-foreground sm:text-base">
                    {formatLedgerAmount(balance.amount, { assetLabel: ticker })}
                  </span>
                  <p className="text-xs text-muted-foreground">
                    {t('balance.availableShort')}:{' '}
                    {balance.availableAmount == null
                      ? t('balance.amountUnknown')
                      : formatLedgerAmount(balance.availableAmount, { assetLabel: ticker })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('balance.reserved')}:{' '}
                    {reserved == null ? t('balance.amountUnknown') : formatLedgerAmount(reserved, { assetLabel: ticker })}
                  </p>
                  {isUnallocatedNetwork(balance.network) ? (
                    <p className="text-xs text-muted-foreground">{historicalLabel}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">{networkLabel}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
